import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { ScaleLoader } from "react-spinners";
import React, { useRef, useEffect } from "react";
import useCountdownTimer from "utils/hooks/use-countdown-timer";

const OTP_INPUT_BASE =
  "w-11 h-11 text-center text-[18px] font-semibold border-2 rounded-ns bg-shade-50 text-ink outline-none transition-colors duration-200";
const OTP_INPUT_OK =
  "border-rule-strong focus:border-accent focus:shadow-[0_0_0_3px_rgba(123,94,167,0.12)]";
const OTP_INPUT_ERR = "border-danger";

const RESEND_COOLDOWN = 90;

const ConfirmSignup = ({ onSubmit, loading, handleResendCode }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { timeLeft, isRunning, resetTimer } = useCountdownTimer(RESEND_COOLDOWN, () => {});

  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remaining.toString().padStart(2, "0")}`;
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !event.target.value && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleInput = (index) => {
    if (index < 5 && inputRefs.current[index].value) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    pasted.split("").forEach((char, i) => {
      if (inputRefs.current[i]) {
        inputRefs.current[i].value = char;
        inputRefs.current[i].dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
    const lastFilled = Math.min(pasted.length, 5);
    inputRefs.current[lastFilled]?.focus();
  };

  const onSubmitForm = (data) => {
    const code = [
      data.code0, data.code1, data.code2,
      data.code3, data.code4, data.code5,
    ].join("");
    onSubmit({ confirmationCode: code });
  };

  const onResendCodeSuccess = () => {
    resetTimer(RESEND_COOLDOWN);
    toast.success("OTP has been sent to your email.", { position: "top-right" });
  };

  const hasOtpError =
    errors.code0 || errors.code1 || errors.code2 ||
    errors.code3 || errors.code4 || errors.code5;

  return (
    <>
      <div className="mb-5">
        <h2 className="text-[20px] font-bold text-ink mb-1">Check your email</h2>
        <p className="text-xs text-muted">
          Enter the 6-digit code we sent to your registered email address.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmitForm)} noValidate>
        <div className="flex justify-between gap-2 mb-1">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <input
              key={index}
              {...register(`code${index}`, { required: true, maxLength: 1 })}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="1"
              onInput={() => handleInput(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`${OTP_INPUT_BASE} ${hasOtpError ? OTP_INPUT_ERR : OTP_INPUT_OK}`}
              ref={(el) => {
                inputRefs.current[index] = el;
                register(`code${index}`).ref(el);
              }}
            />
          ))}
        </div>

        {hasOtpError && (
          <p role="alert" className="text-danger text-[11px] mt-1 mb-3">
            Please enter the complete 6-digit code.
          </p>
        )}

        <div className="flex items-center justify-between mt-3 mb-5">
          <span className="text-[12px] text-muted">
            {isRunning ? (
              <>Code expires in <span className="font-semibold text-muted">{formatTime(timeLeft)}</span></>
            ) : (
              "Code expired."
            )}
          </span>
          <button
            type="button"
            disabled={isRunning}
            onClick={() => handleResendCode(onResendCodeSuccess)}
            className="text-[12px] text-accent font-medium hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Resend code
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full h-12 rounded-ns bg-accent text-white text-[15px] font-bold relative border border-rule-strong  transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className={loading ? "opacity-0" : "opacity-100"}>Verify →</span>
          {loading && (
            <ScaleLoader
              loading
              height={16}
              color="white"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          )}
        </button>
      </form>
    </>
  );
};

export default ConfirmSignup;
